import DirectionsBoat from "@mui/icons-material/DirectionsBoat";
import DirectionsBus from "@mui/icons-material/DirectionsBus";
import Flight from "@mui/icons-material/Flight";
import Hotel from "@mui/icons-material/Hotel";
import Place from "@mui/icons-material/Place";
import type { AutocompleteInputChangeReason } from "@mui/material";
import { Autocomplete, Box, Paper, TextField, ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import type { PaperProps } from "@mui/material/Paper";
import type { FC, MouseEvent, SyntheticEvent } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ProviderProductType } from "../../admin/providers/providersApi";

export interface Product {
  readonly id: string;
  readonly name: string;
  readonly provider: {
    readonly name: string;
    readonly productType?: ProviderProductType | null;
  };
}

const PRODUCT_TYPE_FILTERS = ["Hotel", "Ferry", "Bus", "Plane", "Attraction"] as const;
type ProductTypeFilter = (typeof PRODUCT_TYPE_FILTERS)[number];

const PROVIDER_TYPE_TO_FILTER: Record<ProviderProductType, ProductTypeFilter> = {
  [ProviderProductType.HOTEL]: "Hotel",
  [ProviderProductType.HOTEL_VAT_NP]: "Hotel",
  [ProviderProductType.WATERBORNE_TRANSPORT_VAT_NP]: "Ferry",
  [ProviderProductType.WATERBORNE_TRANSPORT_VAT_0]: "Ferry",
  [ProviderProductType.BUS_TRANSPORT_VAT_NP]: "Bus",
  [ProviderProductType.BUS_TRANSPORT_VAT_8]: "Bus",
  [ProviderProductType.PLANE_TICKET_VAT_0]: "Plane",
  [ProviderProductType.PLANE_TICKET_VAT_NP]: "Plane",
  [ProviderProductType.ATTRACTION]: "Attraction",
};

function productMatchesFilter(product: Product, filter: ProductTypeFilter): boolean {
  const providerType = product.provider.productType;
  if (providerType == null) {
    return true;
  }
  const productFilter = PROVIDER_TYPE_TO_FILTER[providerType];
  return productFilter === filter;
}

const FILTER_ICONS: Record<ProductTypeFilter, FC<{ fontSize?: "small" | "medium" | "large" }>> = {
  Bus: DirectionsBus,
  Plane: Flight,
  Hotel: Hotel,
  Ferry: DirectionsBoat,
  Attraction: Place,
};

const FILTER_LABELS: Record<ProductTypeFilter, string> = {
  Bus: "Bilety autokarowe",
  Plane: "Bilety lotnicze",
  Hotel: "Hotele",
  Ferry: "Transport morski",
  Attraction: "Atrakcje",
};

interface Props {
  readonly value: string | undefined;
  readonly products: Product[];
  readonly error?: boolean;
  readonly disabled?: boolean;
  readonly fullWidth?: boolean;
  readonly disableClearable?: boolean;
  readonly inputValue?: string;
  readonly onInputChange?: (event: SyntheticEvent, value: string, reason: AutocompleteInputChangeReason) => void;
  readonly minWidth?: string | number;
  readonly size?: "small" | "medium";
  readonly placeholder?: string;
  readonly readonly?: boolean;
  readonly onChange: (productId: string | undefined, product: Product | undefined) => void;
}

const DEFAULT_FILTER: ProductTypeFilter = "Hotel";

function productToFilter(product: Product): ProductTypeFilter | null {
  const providerType = product.provider.productType;
  if (providerType == null) {
    return null;
  }
  return PROVIDER_TYPE_TO_FILTER[providerType];
}

function pickNonEmptyFilter(products: Product[], current: ProductTypeFilter): ProductTypeFilter {
  if (products.length === 0) {
    return current;
  }
  if (products.some((p) => productMatchesFilter(p, current))) {
    return current;
  }
  return PRODUCT_TYPE_FILTERS.find((filter) => products.some((p) => productMatchesFilter(p, filter))) ?? current;
}

const ProductSelect: FC<Props> = ({
  value,
  products,
  error,
  fullWidth,
  disableClearable,
  minWidth,
  placeholder = "Produkt",
  size,
  readonly,
  onChange,
  inputValue,
  onInputChange,
  disabled,
}) => {
  const selectedProduct = useMemo(() => {
    if (value == null) {
      return null;
    }
    return products.find((p) => p.id === value) ?? null;
  }, [products, value]);

  const [typeFilter, setTypeFilter] = useState<ProductTypeFilter>(() => {
    const selectedFilter = selectedProduct ? productToFilter(selectedProduct) : null;
    return pickNonEmptyFilter(products, selectedFilter ?? DEFAULT_FILTER);
  });

  useEffect(() => {
    const selectedFilter = selectedProduct ? productToFilter(selectedProduct) : null;
    const desiredFilter = pickNonEmptyFilter(products, selectedFilter ?? DEFAULT_FILTER);
    setTypeFilter((current) => (desiredFilter !== current ? desiredFilter : current));
  }, [products, selectedProduct]);

  const shouldShowTypeFilter = useMemo(() => {
    const categories = products
      .map((p) => p.provider.productType)
      .filter((t) => t != null)
      .map((t) => PROVIDER_TYPE_TO_FILTER[t]);
    const uniqueCategories = new Set(categories);
    return uniqueCategories.size > 1;
  }, [products]);

  const filteredProducts = useMemo(
    () => products.filter((p) => productMatchesFilter(p, typeFilter)),
    [products, typeFilter],
  );

  const handleFilterChange = useCallback((_: MouseEvent<HTMLElement>, newFilter: ProductTypeFilter | null) => {
    if (newFilter == null) {
      return;
    }
    setTypeFilter(newFilter);
  }, []);

  const renderPaperWithFilter = useCallback(
    (props: PaperProps) => (
      <Paper {...props}>
        {shouldShowTypeFilter ? (
          <Box
            sx={{
              px: 1,
              pt: 0.5,
              pb: 0.5,
              borderBottom: 1,
              borderColor: "divider",
            }}
            onMouseDown={(e) => e.preventDefault()}
          >
            <ToggleButtonGroup
              value={typeFilter}
              exclusive
              onChange={handleFilterChange}
              aria-label="Typ produktu"
              size="small"
              disabled={readonly}
              sx={{ width: "100%", display: "flex" }}
            >
              {PRODUCT_TYPE_FILTERS.map((filter) => {
                const Icon = FILTER_ICONS[filter];
                return (
                  <Tooltip key={filter} title={FILTER_LABELS[filter]}>
                    <ToggleButton value={filter} aria-label={FILTER_LABELS[filter]} sx={{ flex: 1 }}>
                      <Icon fontSize="small" />
                    </ToggleButton>
                  </Tooltip>
                );
              })}
            </ToggleButtonGroup>
          </Box>
        ) : null}
        {props.children}
      </Paper>
    ),
    [typeFilter, handleFilterChange, readonly, shouldShowTypeFilter],
  );
  return (
    <Autocomplete
      disablePortal
      multiple={false}
      fullWidth={fullWidth}
      disableClearable={disableClearable}
      size={size}
      sx={{ minWidth, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
      readOnly={readonly}
      value={selectedProduct}
      inputValue={inputValue}
      onInputChange={onInputChange}
      renderInput={(params) => <TextField {...params} label={placeholder} error={error} />}
      options={filteredProducts}
      isOptionEqualToValue={(o, v) => o?.id === v?.id}
      getOptionKey={(o) => o?.id}
      getOptionLabel={(o) => o?.name}
      onChange={(e, selected) => onChange(selected?.id, selected ?? undefined)}
      slots={{ paper: renderPaperWithFilter }}
      disabled={disabled}
    />
  );
};

export default ProductSelect;
